import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import bundledCss from "../styles.css?inline";
import interLatin from "../assets/fonts/inter-latin.woff2?url";
import oswaldLatin from "../assets/fonts/oswald-latin.woff2?url";
import playfairLatin from "../assets/fonts/playfair-display-italic-latin.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { FAVICON, LOGO_380, LOGO_512, SITE_URL } from "../lib/site";

const FONT_PRELOADS = [oswaldLatin, interLatin, playfairLatin] as const;

// SSR: full CSS string. Client: reuse the already-inlined <style> so we do not
// duplicate ~90KB of CSS inside the JS chunk (Lighthouse unused JavaScript).
const appCss = import.meta.env.SSR
  ? bundledCss
  : (document.getElementById("app-css")?.innerHTML ?? "");

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#1a1a1a" },
      { name: "format-detection", content: "telephone=yes" },
      { property: "og:site_name", content: "Jack's Lock Service" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "google-site-verification",
        content: "6WgeiZuPJ0vmP9o9gvtGjY6wF--UmM6peCUjqU8kuD8",
      },
    ],
    links: [{ rel: "icon", href: FAVICON, type: "image/png" }],
    scripts: [
      {
        children: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          var gtagEvents = ['pointerdown','keydown','touchstart'];
          function loadGtag() {
            if (window.__gtagLoaded) return;
            window.__gtagLoaded = true;
            gtagEvents.forEach(function (e) {
              window.removeEventListener(e, loadGtag, { capture: true });
            });
            var s = document.createElement('script');
            s.src = 'https://www.googletagmanager.com/gtag/js?id=G-F2QWTZP27Q';
            s.async = true;
            s.fetchPriority = 'low';
            s.onload = function () {
              gtag('js', new Date());
              gtag('config', 'G-F2QWTZP27Q');
            };
            document.head.appendChild(s);
          }
          gtagEvents.forEach(function (e) {
            window.addEventListener(e, loadGtag, { once: true, passive: true, capture: true });
          });
        `,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        {/* Charset must precede the inlined CSS so the parser does not buffer the preload. */}
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="preload"
          as="image"
          href={LOGO_380}
          type="image/webp"
          media="(max-width: 1023px)"
          fetchPriority="high"
        />
        <link
          rel="preload"
          as="image"
          href={LOGO_512}
          type="image/webp"
          media="(min-width: 1024px)"
          fetchPriority="high"
        />
        <link rel="canonical" href={`${SITE_URL}/`} />
        {FONT_PRELOADS.map((href) => (
          <link
            key={href}
            rel="preload"
            as="font"
            type="font/woff2"
            href={href}
            crossOrigin="anonymous"
            fetchPriority="low"
          />
        ))}
        <style id="app-css" dangerouslySetInnerHTML={{ __html: appCss }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </>
  );
}
